import type { ILocator } from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import { NavigationCollectionType, type IContentLocation } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import type { ICirrusReaderBookmarkData } from "../shared/CirrusReaderModel";

export const useCirrusBookmarking = () => {
    let hydrated = false;
    const bookmarkStore = useCirrusBookmarksStore();

    const { $cirrusReaderView, $cirrusReaderPublication } = useNuxtApp();

    const bookmarkAnnotationLayer = useAnnotationLayer('cirrus-reader__bookmarks-layer', {
        key: 'publication-bookmarks',
        destroyOnLastUnmount: false,
        layerOptions: {
            annotationInputEngineEventsEnabled: true,
            layerStyle: { 'mix-blend-mode': 'multiply' },
        },
        defaultAnnotationOptions: {
            positionStyle: {
                'width': '100%',
                'height': '32px',
            },
            containerClassName: 'cirrus-reader__bookmark-container',
            containerStyle: {
                'left': '0',
                'width': '32px',
                'height': '32px',
            },
        },
    });

    const initBookmarksLayer = async () => {
        if (hydrated) return;
        if (!$cirrusReaderPublication.colibrioReaderPublication) return;

        const publicationId = $cirrusReaderPublication.id;
        const bookmarks = bookmarkStore.bookmarks.filter(b => b.publicationId === publicationId);

        for (const b of bookmarks) {
            if (!bookmarkAnnotationLayer.hasAnnotation(b.locator)) {
                bookmarkAnnotationLayer.createAnnotation(b.locator, b);
            }
        }

        hydrated = true;
    };

    bookmarkAnnotationLayer.on('annotationClick', (event) => {
        event.preventDefault()
        event.stopPropagation();
        deleteBookmark(event.annotation.getCustomData()?.locator);
    });

    const addBookmark = async (locator: ILocator | string): Promise<ICirrusReaderBookmarkData | undefined> => {
        let bookmarkData: ICirrusReaderBookmarkData | undefined = undefined;

        if (typeof locator === 'string') {
            locator = (await $cirrusReaderPublication.colibrioReaderPublication.fetchContentLocation(locator)).getLocator();
        }

        const location: IContentLocation | undefined | null = await $cirrusReaderPublication.colibrioReaderPublication.fetchContentLocation(locator);
        const locationContent = await location?.fetchContentResolver();
        const textExtract = `${locationContent?.getTextContent()}${locationContent.getTextContentAfter(250)}`;

        if (location) {
            const fetchResult = await location.fetchNavigationItemReferences({
                collectionTypes: [NavigationCollectionType.TOC],
                greedy: true,
            });

            const itemsInRange = fetchResult.getItemsInRange();
            let bookSection: string | undefined;
            if (itemsInRange && itemsInRange[0]) {
                bookSection = itemsInRange[0].getNavigationItem().getTextContent();
            }

            bookmarkData = {
                publicationId: $cirrusReaderPublication.id,
                locator: location.getLocator().toString(),
                bookSection,
                text: textExtract.substring(0, 250),
            };
        }

        if (bookmarkData) {
            bookmarkAnnotationLayer.createAnnotation(bookmarkData.locator, bookmarkData);
            useCirrusBookmarksStore().addBookmark(bookmarkData);
        }

        return bookmarkData;
    }

    
    const deleteBookmark = async (locator: string): Promise<void> => {
        if (locator) bookmarkAnnotationLayer.deleteAnnotation(locator);
        if (locator) useCirrusBookmarksStore().deleteBookmark(locator);
    }

    const bookmarkLocator = (locator: string): void  => {
        addBookmark(locator)?.catch(console.warn)
    }

    const bookmarkTextLocation = (
        textToBookmark: string,
        documentIndex: number,
    ) => {
        useCirrusSearch().searchPublication(textToBookmark, 1, [documentIndex], false).then(async (resultSet) => {
            if (resultSet[0]) {
                return addBookmark(resultSet[0].locator).then(bookmarkData => {
                    console.log(bookmarkData);
                });
            }
        }).catch(console.error);
        return;
    }

    const bookmarkReadingPosition = async (): Promise<ICirrusReaderBookmarkData | undefined> => {
        const readingPositionLocator = $cirrusReaderView?.getReadingPosition()?.getLocator();
        
        if (!readingPositionLocator) {
            console.warn(`BookmarkReadingPosition(): Unable to get reading position from CirrusReaderView.`);
            return;
        }
        return addBookmark(readingPositionLocator);
    }


    const bookmarkClipboardContents = async (): Promise<void> => {
        const clipboardContents = await navigator.clipboard.readText();
        const cirrusReaderNavigation = useCirrusReaderNavigation();

        if (!clipboardContents) {
            return;
        }
        
        if (cirrusReaderNavigation.readingPositionData?.documentIndex && clipboardContents) {
            bookmarkTextLocation(clipboardContents, cirrusReaderNavigation.readingPositionData.documentIndex);
        }
    }


    return {
        initBookmarksLayer,
        deleteBookmark,
        bookmarkLocator,
        bookmarkReadingPosition,
        bookmarkClipboardContents
    }
}